import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Button, ModalFooter } from 'reactstrap';
import { WatermarkSelection } from '../../../../components';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeModalState,
  startWaterMarkProcessing,
  toggleGalleryWatermark,
  toggleImageWatermark
} from '../../../../modules/actions';
import { getModalData } from '../../../../modules/selectors';
import { IToggleImageWatermarkAction } from '../../../../modules/types';
import { getTranslationKey } from '../../../../modules/utils/helpers';

interface IProps {
  isOpen: boolean;
  onClose?: () => void;
  setWatermarkCallback?: (watermark: string) => void;
  selectedWatermark?: string;
}

export const SelectWatermarkModal = ({
  isOpen,
  onClose,
  setWatermarkCallback,
  selectedWatermark
}: IProps) => {
  const dispatch = useDispatch();
  const { watermarkType, targetID, watermarkID } = useSelector(getModalData);
  const [watermark, changeWatermark] = useState('');
  const [isSaveClicked, changeSaveClicked] = useState(false);
  const [closeModalState, setCloseModalState] = useState(false);

  useEffect(() => {
    changeWatermark(watermarkID);
    if (selectedWatermark) {
      changeWatermark(selectedWatermark);
    }
  }, [watermarkID, selectedWatermark]); // eslint-disable-line

  const closeModal = () => {
    if (onClose) {
      onClose();
      changeWatermark('');
      changeSaveClicked(false);
      setCloseModalState(false);

      return;
    }

    if (watermarkType === 'gallery' && !isSaveClicked) {
      dispatch(
        toggleGalleryWatermark({
          gallery_id: targetID,
          watermark_id: watermarkID,
          isNoRequest: true
        })
      );
    }
    dispatch(
      changeModalState({
        key: 'selectWaterMarkModal',
        state: false,
        modalData: {}
      })
    );
    changeWatermark('');
    changeSaveClicked(false);
    setCloseModalState(false);
  };

  useEffect(() => {
    if (closeModalState) {
      closeModal();
    }
  }, [setCloseModalState, closeModalState]); // eslint-disable-line

  const selectWatermarkHandler = () => {
    changeSaveClicked(true);
    changeWatermark(watermark);
    if (setWatermarkCallback) {
      setWatermarkCallback(watermark);
      setCloseModalState(true);
      dispatch(
        startWaterMarkProcessing({
          watermark_id: watermark,
          gallery_id: targetID
        })
      );

      return;
    }
    if (watermarkType === 'gallery') {
      dispatch(
        toggleGalleryWatermark({
          gallery_id: targetID,
          watermark_id: watermark
        })
      );
      dispatch(
        startWaterMarkProcessing({
          watermark_id: watermark,
          gallery_id: targetID
        })
      );
    }
    if (watermarkType === 'image') {
      const data: IToggleImageWatermarkAction = {
        image_id: targetID
      };
      if (watermark) {
        data.watermark_id = watermark;
      }
      dispatch(toggleImageWatermark(data));
    }
    setCloseModalState(true);
  };

  return (
    <Modal
      centered
      isOpen={isOpen}
      toggle={closeModal}
      wrapClassName="modal-dark"
      size="default"
      zIndex={1700}
    >
      <ModalHeader data-cy="watermark-toggle" toggle={closeModal}>
        {getTranslationKey('watermark.watermark')}
      </ModalHeader>
      <ModalBody>
        <WatermarkSelection
          data-cy="watermark"
          watermark={watermark}
          changeCallback={changeWatermark}
        />
      </ModalBody>
      <ModalFooter>
        <Button
          data-cy="watermarkButton"
          block
          color="success"
          onClick={selectWatermarkHandler}
          className="btn btn-primary w-100-p"
        >
          {getTranslationKey('watermark.select')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SelectWatermarkModal;
